import React, { useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { GuardProvider } from 'react-router-guards'
import { RouteFactory } from './route-factory'

import AuthProvider from '../../contexts/auth'
import ModalProvider from '../../contexts/modal'
import MeProvider from '../../contexts/me'
import PageProvider from '../../contexts/page'
import authGuard from './guards/auth-guard'
import SnackbarProvider from '../../contexts/snackbar'

// Layouts base
import { BaseLayout, LoginLayout, DashboardLayout } from '../layouts'

// Pages
import MakeProductEditor from '../../../main/factories/pages/dashboard/product/product-editor-factory'
import MakeUserEditor from '../../../main/factories/pages/dashboard/user/user-editor-factory'
import MakeTableEditor from '../../../main/factories/pages/dashboard/table/table-editor-factory'
import MakeProfileEditor from '../../../main/factories/pages/dashboard/profile/profile-editor-factory'
import MakeProductList from '../../../main/factories/pages/dashboard/product/product-list-factory'
import MakeSignin from '../../../main/factories/pages/amplify/signin-factory'
import MakeForgotPassword from '../../../main/factories/pages/amplify/forgot-password-factory'
import MakeRecoveryPassword from '../../../main/factories/pages/amplify/recovery-password-factory'
import MakeTableList from '../../../main/factories/pages/dashboard/table/table-list-factory'
import MakeProfileList from '../../../main/factories/pages/dashboard/profile/profile-list-factory'
import MakeUserList from '../../../main/factories/pages/dashboard/user/user-list-factory'
import MakeDoubtEditor from '../../../main/factories/pages/dashboard/doubts/doubt-editor-factory'
import MakeAssessmentEditor from '../../../main/factories/pages/dashboard/assessments/assessment-editor-factory'
import MakeOrders from '../../../main/factories/pages/dashboard/orders/orders-factory'
import MakeHome from '../../../main/factories/pages/dashboard/home-factory'
import MakeConfigNFCe from '../../../main/factories/pages/dashboard/config-nfce/config-nfce-factory'
import MakeCallWaiterEditor from '../../../main/factories/pages/dashboard/call-waiter/call-waiter-editor-factory'
import MakeMenuOrganization from '../../../main/factories/pages/dashboard/menu-organization/menu-organization-factory'
import OrderProvider from '../../contexts/order'
import MakeEstablishmentEditor from '../../../main/factories/pages/dashboard/establishment/establishment-editor-factory'
import MakeTableConfigurationEditor from '../../../main/factories/pages/dashboard/table-configuration/table-configuration-editor-factory'
import MakeClientAssessmentList from '../../../main/factories/pages/dashboard/client-assessments/client-assessment-list-factory'
import LoadingProvider from '../../contexts/loading'
import QueryProvider from '../../contexts/query'
import MakePresencialFinance from '../../../main/factories/pages/dashboard/presencial-finance/orders-factory'
import DashboardProvider from '../../contexts/dashboard'

const RedirectToDashboard = () => {
  const history = useHistory()
  useEffect(() => {
    history.push('/dashboard')
  }, [history])
  return null
}

const Routes = (): JSX.Element => {
  const routes = [
    {
      layout: LoginLayout,
      pages: [
        { path: '/signin', component: MakeSignin },
        { path: '/forgot-password', component: MakeForgotPassword },
        { path: '/recovery-password', component: MakeRecoveryPassword },
      ],
    },
    {
      layout: DashboardLayout,
      pages: [
        { path: '/dashboard', component: MakeHome, meta: { auth: true } },
        { path: '/dashboard/orders', component: MakeOrders, meta: { auth: true } },
        { path: '/dashboard/products', component: MakeProductList, meta: { auth: true } },
        { path: '/dashboard/products/editor', component: MakeProductEditor, meta: { auth: true } },
        { path: '/dashboard/users', component: MakeUserList, meta: { auth: true } },
        { path: '/dashboard/users/editor', component: MakeUserEditor, meta: { auth: true } },
        { path: '/dashboard/tables', component: MakeTableList, meta: { auth: true } },
        { path: '/dashboard/tables/editor', component: MakeTableEditor, meta: { auth: true } },
        { path: '/dashboard/tables/configuration/editor', component: MakeTableConfigurationEditor, meta: { auth: true } },
        { path: '/dashboard/menu-organization', component: MakeMenuOrganization, meta: { auth: true } },
        { path: '/dashboard/profiles', component: MakeProfileList, meta: { auth: true } },
        { path: '/dashboard/profiles/editor', component: MakeProfileEditor, meta: { auth: true } },
        { path: '/dashboard/doubts', component: MakeDoubtEditor, meta: { auth: true } },
        { path: '/dashboard/assessments', component: MakeAssessmentEditor, meta: { auth: true } },
        { path: '/dashboard/call-waiter', component: MakeCallWaiterEditor, meta: { auth: true } },
        { path: '/dashboard/logo-establishment', component: MakeEstablishmentEditor, meta: { auth: true } },
        { path: '/dashboard/clients-assessments', component: MakeClientAssessmentList, meta: { auth: true } },
        { path: '/dashboard/finance/presencial', component: MakePresencialFinance, meta: { auth: true } },
        { path: '/dashboard/finance/config-nfce', component: MakeConfigNFCe, meta: { auth: true } },
      ],
    },
    {
      layout: BaseLayout,
      pages: [
        { path: '/', component: RedirectToDashboard, meta: { auth: true } },
        { path: '*', component: RedirectToDashboard },
      ],
    },
  ]

  return (
    <ModalProvider>
      <MeProvider>
        <SnackbarProvider>
          <AuthProvider>
            <DashboardProvider>
              <QueryProvider>
                <PageProvider>
                  <GuardProvider guards={[authGuard]}>
                    <OrderProvider>
                      <LoadingProvider>
                        <RouteFactory routes={routes} />
                      </LoadingProvider>
                    </OrderProvider>
                  </GuardProvider>
                </PageProvider>
              </QueryProvider>
            </DashboardProvider>
          </AuthProvider>
        </SnackbarProvider>
      </MeProvider>
    </ModalProvider>
  )
}

export default Routes
